import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, MapPin } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { api } from "@/lib/api";

const SERIES = [["plays", "#00F5D4"], ["listeners", "#7B2CBF"], ["flames", "#FF2E63"], ["blind_flames", "#FFB800"], ["visits", "#94A3B8"]];

const Locked = () => (
  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-[14px] bg-[#07090E]/70 text-center" data-testid="insights-locked">
    <Lock className="h-6 w-6 text-[#FFB800]" />
    <p className="max-w-sm text-sm text-slate-300">Monthly trends, listener cities and track-vs-track comparison are included in Artist and Pro.</p>
    <Link to="/dashboard?tab=plan" className="btn-hot !px-5 !py-2 text-sm">Upgrade from €4.99</Link>
  </div>
);

export const Insights = ({ d }) => {
  const [s, setS] = useState(null);
  const locked = d.plan.id === "free";
  useEffect(() => { api.get("/me/insights").then((r) => setS(r.data)); }, []);
  if (!s) return <p className="font-mono text-slate-500">Crunching your numbers…</p>;
  const maxCity = Math.max(1, ...s.cities.map((c) => c.listeners));
  return (
    <div className="relative" data-testid="dash-insights">
      {locked && <Locked />}
      <div className={`space-y-6 ${locked ? "pointer-events-none select-none blur-sm" : ""}`}>
        <div className="panel p-6" data-testid="insights-trends">
          <div className="label-mono mb-4">Monthly trend · last 6 months</div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={s.months}>
                <CartesianGrid stroke="rgba(255,255,255,.06)" />
                <XAxis dataKey="month" stroke="#64748B" fontSize={11} />
                <YAxis stroke="#64748B" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#0F1420", border: "1px solid rgba(255,255,255,.1)", borderRadius: 10 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {SERIES.map(([k, c]) => <Line key={k} type="monotone" dataKey={k} stroke={c} strokeWidth={2} dot={{ r: 3 }} />)}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="grid gap-6 lg:grid-cols-5">
          <div className="panel p-6 lg:col-span-2" data-testid="insights-cities">
            <div className="label-mono mb-4">Where your listeners are</div>
            {s.cities.map((c) => (
              <div key={c.city} className="mb-3 flex items-center gap-3 text-sm">
                <MapPin className="h-3.5 w-3.5 text-[#00F5D4]" /><span className="w-28 truncate">{c.city}</span>
                <div className="h-2 flex-1 rounded-full bg-white/10"><div className="h-full rounded-full bg-gradient-to-r from-[#00F5D4] to-[#FF2E63]" style={{ width: `${(c.listeners / maxCity) * 100}%` }} /></div>
                <span className="led w-8 text-right">{c.listeners}</span>
              </div>
            ))}
            {!s.cities.length && <p className="text-sm text-slate-500">City data appears as listeners tune in.</p>}
            <p className="mt-4 text-xs text-slate-500">{s.known_listeners} unique listeners reached. Cities are estimated from network location.</p>
          </div>
          <div className="panel overflow-x-auto lg:col-span-3" data-testid="insights-tracks">
            <div className="label-mono p-6 pb-2">Track vs track</div>
            <table className="w-full min-w-[560px] text-sm">
              <thead><tr className="border-b border-white/10 text-left font-mono text-[10px] uppercase tracking-widest text-slate-500">
                {["Track", "Plays", "Listeners", "Flames", "Blind", "Flame rate", "Top reaction"].map((h) => <th key={h} className="p-3 font-normal">{h}</th>)}
              </tr></thead>
              <tbody>{s.tracks.map((t, i) => (
                <tr key={t.id} className="border-b border-white/5" data-testid={`insights-track-${t.id}`}>
                  <td className="p-3 font-display font-bold">{i === 0 && s.tracks.length > 1 && <span className="mr-1 text-[#FFB800]">★</span>}{t.title}</td>
                  <td className="p-3 font-mono">{t.plays}</td><td className="p-3 font-mono">{t.listeners}</td>
                  <td className="p-3 font-mono text-[#FF2E63]">{t.flames}</td><td className="p-3 font-mono text-[#FFB800]">{t.blind_flames}</td>
                  <td className="p-3 led">{t.flame_rate}%</td><td className="p-3 font-mono text-xs uppercase">{t.top_reaction || "–"}</td>
                </tr>
              ))}</tbody>
            </table>
            <p className="p-4 text-xs text-slate-500">Flame rate = Flames per 100 listeners. The starred track converts listeners best.</p>
          </div>
        </div>
      </div>
    </div>
  );
};
